/******************************************************************
 * Copyright @ 2023 广州牛师帮文化科技有限公司. All rights reserverd.
 * @file   src\routes\SSO\useSSOLogic.ts
 * @author Gim
 * @date   2023/01/13 11:46
 * @desc   SSO登录逻辑
 ******************************************************************/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Tunnel } from '@/../sso_plugin/src/Tunnel';
import { Paths } from '@/Paths';
import { KnownError } from '@/api/checkReply';
import { CancelHolder } from '@/network';
import qs from 'qs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { TokenHostCheck } from '../../api/edited/TokenHostCheck'; // TORNA-API-VISITOR
import { clearLoginInfo, getFullLoginInfo, useLoginInfo } from "../Login";
import LinkTunnel from './LinkTunnel';
export function useSSOLogic() {
    const { search } = useLocation();
    const { signal, from, title: _title, domainId, tunnel, redirect } = useMemo(() => {
        const { signal, from, title, domainId, tunnel, redirect } = qs.parse(search, { ignoreQueryPrefix: true });
        const _from = typeof from === 'string' ? from : '';
        // history.replaceState(null, '', location.protocol + '//' + location.host + location.pathname);
        return {
            signal: typeof signal === 'string' ? signal : '',
            from: _from,
            title: typeof title === 'string' ? title : '',
            domainId: Number(domainId),
            tunnel: tunnel === 'link' ? 'link' : 'iframe',
            redirect: typeof redirect === 'string' ? redirect : _from,
        };
    }, []);
    const [ICP, setICP] = useState(void 0);
    const [title, setTitle] = useState(_title);
    const [formVisible, setFormVisible] = useState(true);
    const [copyright, setCopyright] = useState(void 0);
    const tunnelRef = useRef();
    if (!tunnelRef.current && tunnel === 'iframe') {
        const turn = new Tunnel({
            signal: signal,
            origin: from,
            window: window.parent,
        });
        turn.send('ready');
        turn.on('icp', icp => setICP(icp));
        turn.on('title', title => setTitle(title !== null && title !== void 0 ? title : _title));
        turn.on('copyright', copyright => setCopyright(copyright));
        turn.on('form_visible', visible => setFormVisible(visible));
        turn.on('refresh_user_info', () => getFullLoginInfo(Number(domainId), true));
        turn.resp('login_info', (refresh = true) => getFullLoginInfo(Number(domainId), refresh));
        tunnelRef.current = turn;
    }
    else if (!tunnelRef.current && tunnel === 'link') {
        tunnelRef.current = new LinkTunnel({ redirect, title: _title, domainId: domainId });
    }
    const token = useLoginInfo('token');
    const checkAccess = (options) => __awaiter(this, void 0, void 0, function* () {
        const tunnel = tunnelRef.current;
        if (!options.token)
            return tunnel.send('reject', 'not_login');
        const params = {
            authorization: options.token,
            targetOrigin: options.from
        };
        yield TokenHostCheck
            .call(params, options.canceller)
            .then(reply => {
            return !!reply.data;
        }).then(pass => {
            if (pass) {
                return getFullLoginInfo(Number(domainId));
            }
            else {
                tunnel.send('reject', 'permission_denied');
            }
        }).then(info => {
            if (info)
                tunnel.send('resolve', info);
        }).catch(err => {
            options.canceller.ignoreCancelErr(err);
        }).catch(err => {
            if (err instanceof KnownError && '' + err.code === '401') {
                tunnel.send('reject', 'permission_denied');
            }
            else if (err instanceof Error) {
                tunnel.send('error', '' + err.message);
            }
            else {
                tunnel.send('error', '' + err);
            }
        });
    });
    useEffect(() => {
        if (typeof signal !== 'string')
            return;
        if (typeof from !== 'string')
            return;
        switch (location.pathname) {
            case Paths.All.SSO_PAGE:
            case Paths.All.SSO_VIEW:
                const canceller = new CancelHolder;
                checkAccess({ from, token, canceller });
                return () => { canceller.cancel(); };
            case Paths.All.SSO_LOGOUT:
                clearLoginInfo();
                tunnelRef.current.send('logout');
                return;
        }
    }, [from, token, location.pathname]);
    return { from, signal, title, ICP, copyright, formVisible, tunnelRef };
}
